import type {
  NaturalDimensions,
  VideoDimensions,
} from '@src/editor/types/aspectRatio';

/**
 * Handles media element positioning and sizing across different aspect ratios
 */
export class MediaPositioner {
  private prevDimensions: VideoDimensions;
  private newDimensions: VideoDimensions;

  /**
   * Create a media positioner
   * @param prevDimensions Previous video dimensions
   * @param newDimensions New video dimensions
   */
  constructor(prevDimensions: VideoDimensions, newDimensions: VideoDimensions) {
    this.prevDimensions = prevDimensions;
    this.newDimensions = newDimensions;
  }

  /**
   * Get default positioning for media elements
   */
  static getDefaultPositioning(): Record<
    'x' | 'y' | 'width' | 'height',
    string
  > {
    return {
      x: '50%',
      y: '50%',
      width: '100%',
      height: '100%',
    };
  }

  /**
   * Get adjusted dimensions for a media element based on natural dimensions
   */
  getAdjustedDimensions(
    naturalDimensions?: NaturalDimensions,
  ): Record<'x' | 'y' | 'width' | 'height', string> {
    const result = { ...MediaPositioner.getDefaultPositioning() };

    // Calculate the adjusted width based on ratio between previous and new dimensions
    let newWidth = this.adjustWidthForNewDimensions(100); // Base width is 100%

    // Adjust width based on natural dimensions if available
    if (naturalDimensions) {
      newWidth = this.adjustWidthForNaturalDimensions(
        newWidth,
        naturalDimensions,
      );
    }

    result.width = `${newWidth}%`;
    return result;
  }

  /**
   * Calculate the adjusted width based on the ratio between previous and new dimensions
   */
  private adjustWidthForNewDimensions(baseWidth: number): number {
    return Math.max(
      100,
      Number(
        (
          (this.prevDimensions.width * baseWidth) /
          this.newDimensions.width
        ).toFixed(4),
      ),
    );
  }

  /**
   * Adjust width based on natural dimensions of the element
   */
  private adjustWidthForNaturalDimensions(
    baseWidth: number,
    naturalDimensions: NaturalDimensions,
  ): number {
    const naturalAspectRatio =
      naturalDimensions.width / naturalDimensions.height;
    const prevAspectRatio =
      this.prevDimensions.width / this.prevDimensions.height;

    return (baseWidth * naturalAspectRatio) / prevAspectRatio;
  }
}
